import { getUserVisibleOrDefaultExceptionMessage, ReadFile } from '@paragon/client-tools';
import { Job, ModelCategory, ProjectLayoutModel } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  archiveProject as archiveLayoutModelProject,
  changesExist,
  componentDesignToLayoutModel,
  getChildrenFinder,
  getDescendants,
  getLayoutModelsWithPendingChanges,
  getQuantityMap,
  loadProjectsAndComponentDesignSummaryLayoutModels,
  nonSynchronizedLayoutModelLoaded,
  ProjectChangeFactory,
  saveAllProjectChanges,
  validateProjectHierarchyModelHasRequiredProperties,
} from '@paragon/layout-models';
import { getClientUrls } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import {
  getScopeComponentDesignGuids,
  getScopedComponentDesignQuantities,
  ProjectActionScope,
  requireSingleScopeProjectGuid,
} from '../projectActionScope';
import {
  hasRepairTrussDesignDrawingTemplateOptionsSelector,
  needsToMakeTrussDesignDrawingTemplateSelectionsSelector,
} from '../selectors/annotationTemplates';
import { componentDesignTimestampsLoaded } from '../slices/componentDesignTimestamps';
import { showAddMiscellaneousFilesDialog, showChooseTrussDesignDrawingTemplatesDialog } from './dialogActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const loadComponentDesignSummaries =
  (componentDesignGuids: readonly string[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { timestamps, componentDesignSummaries } =
      await designService.componentDesignSummaries.fetchBulkWithTimestamps(componentDesignGuids);
    const layoutModels = componentDesignSummaries.map((summary) => componentDesignToLayoutModel(summary));

    dispatch(nonSynchronizedLayoutModelLoaded(layoutModels));
    dispatch(componentDesignTimestampsLoaded(timestamps));
  };

export const saveChanges = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  for (const changes of new ProjectChangeFactory(state).getAffectedProjects()) {
    const projectLayoutModel = getLayoutModelsWithPendingChanges(state)[changes.projectGuid];
    if (!projectLayoutModel) {
      return;
    }

    const validationErrors = validateProjectHierarchyModelHasRequiredProperties(projectLayoutModel);
    if (validationErrors !== '') {
      dispatch(enqueueError(validationErrors));
      return;
    }

    const intermediateModels = getDescendants(changes.projectGuid, getChildrenFinder(state)).filter(
      (model) => model.category === ModelCategory.IntermediateModel,
    );

    for (const intermediateModel of intermediateModels) {
      const validationErrors = validateProjectHierarchyModelHasRequiredProperties(intermediateModel);
      if (validationErrors !== '') {
        dispatch(enqueueError(validationErrors));
        return;
      }
    }
  }

  const spinnerId = dispatch(addSpinnerMessage('Saving project...'));

  try {
    if (changesExist(state)) {
      await dispatch(saveAllProjectChanges());
    }
  } catch (error) {
    console.error(error);
    dispatch(
      enqueueError(
        getUserVisibleOrDefaultExceptionMessage(
          error,
          'Error saving.  Please try again or contact Paragon Customer Support.',
        ),
      ),
    );
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const duplicateProject =
  (projectGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Duplicating project...'));

    try {
      const project = await designService.projects.duplicate(projectGuid);
      dispatch(loadProjectsAndComponentDesignSummaryLayoutModels([project]));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error duplicating project')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const createJob =
  (
    type: 'Seal' | 'Repair',
    askForMiscellaneousFiles: boolean,
    scope: ProjectActionScope,
    trussDesignDrawingTemplateId: number | null = null,
    trussDesignDrawingOverflowTemplateId: number | null = null,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    if (!state.logInData.loggedInUser) {
      return;
    }

    const operation = type === 'Seal' ? 'requesting seals' : 'requesting repair';
    const capitalizedOperation = operation.charAt(0).toUpperCase() + operation.slice(1);
    const spinnerMessage = `${capitalizedOperation}...`;

    const componentDesignGuids = getScopeComponentDesignGuids(scope);
    const projectGuid = requireSingleScopeProjectGuid(scope, dispatch);

    if (projectGuid == null) {
      return;
    }

    const project = getState().layoutModels[projectGuid] as ProjectLayoutModel;
    const projectValidationErrors = validateProjectHierarchyModelHasRequiredProperties(project);

    if (projectValidationErrors !== '') {
      dispatch(enqueueError(projectValidationErrors));
      return;
    }

    const componentDesignQuantities = getScopedComponentDesignQuantities(scope, getQuantityMap(state), 'fullQuantity');

    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));
    try {
      const jobForUpload = projectLayoutModelToJob(
        type,
        project,
        componentDesignGuids.length,
        state.logInData.loggedInUser.location,
      );

      const jobId = await designService.componentDesigns.postCreateJob({
        jobType: type,
        componentDesignGuids,
        componentDesignQuantities,
        trussDesignDrawingTemplateId,
        trussDesignDrawingOverflowTemplateId,
        serializedJobForUpload: JSON.stringify(jobForUpload),
        sourceProjectGuid: project.guid,
        name: jobForUpload.name ?? project.name,
        shareWithOrganization: null,
        sendEmailForAnalysisErrorsOrOverCapacity: true,
      });
      const urls = getClientUrls(state);
      if (askForMiscellaneousFiles) dispatch(showAddMiscellaneousFilesDialog(jobId, urls.reviewUrl));
      else window.open(urls.reviewUrl, '_blank');
    } catch (e) {
      dispatch(
        enqueueError(
          getUserVisibleOrDefaultExceptionMessage(e, `Error ${operation}. Try again or notify Paragon support.`),
        ),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

/** Requests seals for the scoped components, first collecting template selections when the organization has them. */
export const requestSeals =
  (scope: ProjectActionScope): ThunkActionVoid =>
  (dispatch, getState) => {
    if (requireSingleScopeProjectGuid(scope, dispatch) == null) return;

    if (needsToMakeTrussDesignDrawingTemplateSelectionsSelector(getState())) {
      dispatch(showChooseTrussDesignDrawingTemplatesDialog('RequestSeals', scope));
    } else {
      void dispatch(createJob('Seal', false, scope));
    }
  };

/** Requests a repair for the scoped components, first collecting template selections when the organization has them. */
export const requestRepair =
  (scope: ProjectActionScope): ThunkActionVoid =>
  (dispatch, getState) => {
    if (requireSingleScopeProjectGuid(scope, dispatch) == null) return;

    if (hasRepairTrussDesignDrawingTemplateOptionsSelector(getState())) {
      dispatch(showChooseTrussDesignDrawingTemplatesDialog('RequestRepair', scope));
    } else {
      void dispatch(createJob('Repair', true, scope));
    }
  };

function projectLayoutModelToJob(
  jobType: 'Seal' | 'Repair',
  projectLayoutModel: ProjectLayoutModel,
  numberOfDesigns: number,
  billingLocation: string,
): Partial<Job> {
  if (!projectLayoutModel.region) throw new Error('Region is required');

  return {
    ...projectLayoutModel,
    regions: [projectLayoutModel.region],
    jobType,
    jobPriority: 'Normal',
    designProgram: 'Paragon',
    ownerOrganization: projectLayoutModel.organization,
    numberOfDesigns,
    billingLocation,
    sourceProjectGuid: projectLayoutModel.guid,
  };
}

export const openProject =
  (projectGuid: string): AsyncThunkActionVoid =>
  async (_, getState) => {
    const state = getState();
    const project = getLayoutModelsWithPendingChanges(state)[projectGuid];
    if (!project) return;

    const designClientUrl = getClientUrls(state).designUrl;
    window.open(`${designClientUrl}${projectGuid}/0/${project.type}/${projectGuid}/0`, '_blank');
  };

export const archiveProject =
  (projectGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    try {
      await dispatch(archiveLayoutModelProject(projectGuid));
    } catch (error) {
      dispatch(
        enqueueError(
          getUserVisibleOrDefaultExceptionMessage(
            error,
            'Error while archiving project.  Please contact Paragon Customer Support.',
          ),
        ),
      );
    }
  };

export const importPpf =
  (projectGuid: string, base64Ppf: ReadFile): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Importing PPF...'));

    try {
      await designService.projects.importPpf(projectGuid, base64Ppf);
      window.location.reload();
    } catch {
      dispatch(enqueueError('Error importing PPF.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
